import { Box } from '@mui/material';
import { ApplicantTable } from 'shared/components/shift-detail-modal/components/tabs/applicants/applicant-table';
import { CancelledTable } from 'shared/components/shift-detail-modal/components/tabs/cancelled/cancelled-table';
import { ClaimantTable } from 'shared/components/shift-detail-modal/components/tabs/claimants/claimant-table';
import {
  ShiftDetailTab,
  ShiftDetailTabData,
} from 'shared/components/shift-detail-modal/components/types';
import { ShiftAddFlexWorker } from 'shared/components/shift-detail-modal/components/tabs/add-flex-worker/shift-add-flex-worker';
import { LwTabIndicator } from 'redesign';
import { useGetAddShiftClaimActions } from 'shared/components/shift-detail-modal/hooks/shared/use-get-add-shift-claim-actions';
import { EmploymentType } from '@types';
import { ShiftDetail } from 'api/services/shift/schema/shift-detail.schema';
import { useTranslation } from 'react-i18next';
import { useHqGetCancelledActions } from './actions/use-hq-get-cancelled-actions';
import { useHqGetClaimantActions } from './actions/use-hq-get-claimant-actions';
import { useHqGetApplicantActions } from './actions/use-hq-get-applicant-actions';
import { mapShiftDetails } from '../shared/map-shift-details';
import { ShiftDetailsApplicant } from '../../components/tabs/applicants/applicant-table.types';
import { LogsTabs } from '../../components/shift-logs/logs-tabs/logs-tabs';
import { useGetLogTabs } from '../../components/shift-logs/hooks/use-get-log-tabs/use-get-log-tabs';

const useHqGetShiftDetailsTabs = (shiftData: ShiftDetail) => {
  const { getCancelledActions } = useHqGetCancelledActions();
  const { getClaimantActions, NoShowDialog, ChooseFlexWorkerFlexPools } = useHqGetClaimantActions(
    shiftData.companyId
  );
  const { getApplicantActions, BulkAcceptShiftApplicationsDialog } = useHqGetApplicantActions();
  const flexWorkerTableActions = useGetAddShiftClaimActions({ isHq: true });
  const logTabs = useGetLogTabs(shiftData.history);
  const { t } = useTranslation('web-common');

  const mappedWorkerData = mapShiftDetails(shiftData);

  const claimTableDetails = {
    mutableUntilDate: shiftData.mutableUntilDate,
    isFreelance: shiftData.isFreelance,
    isTempwork: shiftData.isTempwork,
    isEmployee: shiftData.isEmployee,
    id: shiftData.id,
    startDate: shiftData.startDate,
    startTime: shiftData.startTime,
  };

  const applicantTableDetail: ShiftDetailsApplicant = {
    startDate: shiftData.isoStartDateTime,
    employmentType: (shiftData.isFreelance ? 'FREELANCER' : 'TEMPWORKER') as EmploymentType,
    defaultHourlyRate: (shiftData.isFreelance
      ? shiftData.freelanceProperties?.hourlyRateCents
      : shiftData.tempWorkProperties?.grossHourlyRateCents) as number,
    isEmployee: shiftData.isEmployee,
    isFreelance: shiftData.isFreelance,
    isTempwork: shiftData.isTempwork,
  };

  const addWorkerTableDetails = {
    shiftId: shiftData.id,
    isFreelance: shiftData.isFreelance,
    isTempWork: shiftData.isTempwork,
    isEmployee: shiftData.isEmployee,
  };

  if (!shiftData) {
    return [];
  }

  const tabs = [
    {
      id: ShiftDetailTab.TEAM,
      tabLabel: t('shift_tabs.team'),
      element: (
        <>
          {NoShowDialog}
          {ChooseFlexWorkerFlexPools}
          <ClaimantTable
            shiftDetails={claimTableDetails}
            shiftClaimants={mappedWorkerData.claimants}
            getActions={getClaimantActions}
          />
        </>
      ),
    },
    {
      id: ShiftDetailTab.APPLICATIONS,
      tabLabel: (
        <>
          {BulkAcceptShiftApplicationsDialog}
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
          >
            {t('shift_tabs.applications')}
            {mappedWorkerData.isThereApplicants ? (
              <LwTabIndicator>{mappedWorkerData.pendingApplicants?.length}</LwTabIndicator>
            ) : null}
          </Box>
        </>
      ),
      element: (
        <ApplicantTable
          shiftDetails={applicantTableDetail}
          applicants={mappedWorkerData?.applicants}
          getActions={getApplicantActions}
        />
      ),
    },
    {
      id: ShiftDetailTab.CANCELS,
      tabLabel: t('shift_tabs.cancellations'),
      element: (
        <CancelledTable
          cancelledCandidates={mappedWorkerData?.cancelled}
          getActions={getCancelledActions}
        />
      ),
    },
    {
      id: ShiftDetailTab.ADD,
      tabLabel: t('shift_tabs.add_flex_worker'),
      element: (
        <ShiftAddFlexWorker
          shiftDetails={addWorkerTableDetails}
          getRowActions={flexWorkerTableActions}
          isActorOGPUser={false}
        />
      ),
    },
    {
      id: ShiftDetailTab.LOGS,
      tabLabel: <span data-testid="tab-logs">{t('shift_tabs.logs')}</span>,
      element: <LogsTabs tabs={logTabs} />,
    },
  ];

  return tabs as ShiftDetailTabData[];
};

export { useHqGetShiftDetailsTabs };
